import {call, fork, put, select, takeEvery} from "redux-saga/effects";
import {
    IChangeLanguageAndMarket,
    initSettings,
    ISetCustomerModeSettingsAction,
    setCustomerMode,
    setLanguage,
    setLanguageAndMarket,
    SettingsAction
} from "./settings.actions";
import {ISettingsContainerState} from "./settings";
import {IStoreState} from "@montana/store/store";
import {getLocalStorageItem, setLocalStorageItem} from "@montana/helpers/localstorage.helper";
import {Keys} from "@montana/constants/localstorage";
import {LOCATION_CHANGE, LocationChangeAction} from "connected-react-router";
import {isBrowser} from "@montana/helpers/browser.helper";
import {IDynamicSettings} from "@montana/models/app-settings";
import {Location} from "history";
import {IRouterContainerState} from "../router";
import {IDynamicSettingsContainerState} from "../dynamic-settings/dynamic-settings";
import {CUSTOMER_MODE} from "@montana/constants/customer-mode";
import {OmitConfig} from "@montana/models/dev";
import {connectServiceFunctions} from "../base/base.sagas";
import {fetchPageDataAvailable} from "@montana/services/content";
import {removeTrailingSlash} from "@montana/helpers/location.helper";
import {replaceLanguageInURL} from "@montana/helpers/language.helper";
import {getPageContent} from "@montana/containers/page/page.sagas";
import {setMarketCookie} from "@montana/services/set-cookie";
import {IHrefLang} from "@montana/models/generated/cms.interface";
import {transformUrlToLanguageFallback} from "@montana/helpers/market.helper";

function* onPersistSettings() {
    const settings: ISettingsContainerState = yield select((store: IStoreState) => store.settings);
    setLocalStorageItem(Keys.SETTINGS, JSON.stringify(settings));
}

function* isContractorContext(location: Location) {
    const pathname = location.pathname + location.search;
    const {links}: IDynamicSettings = yield select((s: IStoreState) => s.dynamicSettings);
    const contractorFrontPage = removeTrailingSlash(links?.contractorFrontPage || '');

    const isContractor = contractorFrontPage ? pathname.indexOf(contractorFrontPage) > -1 : false;
    if (isBrowser) {
        if (isContractor) {
            setLocalStorageItem(Keys.CUSTOMER_TYPE, CUSTOMER_MODE.PROFESSIONAL);
        } else {
            setLocalStorageItem(Keys.CUSTOMER_TYPE, CUSTOMER_MODE.PRIVATE);
        }
    }
    return isContractor;
}

function* onPageInit() {
    const settings = getLocalStorageItem(Keys.SETTINGS);

    const {
        markets,
        location,
        languages
    }: IDynamicSettingsContainerState & IRouterContainerState = yield select((s: IStoreState) => ({...s.dynamicSettings, ...s.router}));

    const selectedMarket = markets?.find(market => market.isSelected);
    const selectedLanguage = languages?.find(language => language.isSelected);

    const customerMode = yield getCustomerMode(location);
    const newSettings = {market: selectedMarket, customerMode, language: selectedLanguage?.languageURLSegment};

    yield put(initSettings(settings ? {...JSON.parse(settings), ...newSettings} : newSettings));
    yield put(setLanguageAndMarket(selectedLanguage?.languageURLSegment!, selectedMarket!));

}

function* getCustomerMode(location: Location) {
    const {customerMode}: ISettingsContainerState = yield select((s: IStoreState) => s.settings);

    const asContractor = yield isContractorContext(location);

    /*if (staticSettings.b2b) {
        return CUSTOMER_MODE.B2B;
    }*/
    if ((customerMode === CUSTOMER_MODE.PROFESSIONAL && asContractor) || (customerMode === CUSTOMER_MODE.PRIVATE && !asContractor)) {
        return customerMode;
    }

    return asContractor ? CUSTOMER_MODE.PROFESSIONAL : CUSTOMER_MODE.PRIVATE;
}

export function* onLocationChange(action: LocationChangeAction) {
    if (action.payload.isFirstRendering) {
        return;
    }

    const newCustomerMode = yield getCustomerMode(action.payload.location);
    yield put(setCustomerMode(newCustomerMode));
}

function* onLanguageAndMarketChange(action: IChangeLanguageAndMarket) {
    if (!isBrowser) {
        return;
    }

    const {market, language, customerMode}: ISettingsContainerState = yield select(
        (store: IStoreState) => store.settings
    );

    const { links }: IDynamicSettingsContainerState = yield select((s: IStoreState) => s.dynamicSettings);

    yield put(setLanguage(action.language));

    const {hrefLangs} = yield select((store: IStoreState) => store.page);

    const currentUrl = new URL(location.href);
    const rawSwitchToLangUrl = hrefLangs?.find((item: IHrefLang) => item.languageCode?.split('-')?.[0] === action.language)?.url;
    const switchSearchParams = currentUrl.search;
    const switchToLangUrlPathname = rawSwitchToLangUrl ? rawSwitchToLangUrl.split('?')[0] : '';
    const rawProductPageUrl = customerMode === 'professional' ? links?.contractorProductPage : links?.productPage;
    const productPageUrl = rawProductPageUrl ? transformUrlToLanguageFallback(rawProductPageUrl, action.language) : '';

    // Handle both language and market changes simultaneously
    if (
        market?.marketId !== action.market.marketId &&
        language !== action.language &&
        isBrowser
    ) {

        // set a server cookie with the new market
        const cookieResponse = yield setServerSideMarketCookie(action.market.marketId || '');

        // Get the new URL by replacing the language in the current URL with the new language URL segment.
        const newUrl = replaceLanguageInURL(
            currentUrl,
            action.language!
        );

        const {customerMode}: ISettingsContainerState = yield select(
            (s: IStoreState) => s.settings
        );

        const productId = newUrl.searchParams.get("product");

        const [cfetchPageDataAvailable]: [
            OmitConfig<typeof fetchPageDataAvailable>
        ] = yield connectServiceFunctions(fetchPageDataAvailable);

        const pageResponse: Response = yield call(
            cfetchPageDataAvailable,
            newUrl.toString(),
            productId  ? productPageUrl : switchToLangUrlPathname,
            customerMode
        );

        const pageAvailable = pageResponse.ok;

        if (cookieResponse.ok) {
            if (pageAvailable) {
                // Add search parameters to the url if they exist for product pages
                location.href = newUrl.toString();
            } else {
                location.href = "/" + action.language;
            }
        }
    } else if (market?.marketId !== action.market.marketId) {

        // fetch the page data for the new market
        yield call(getPageContent, currentUrl.toString(), false);

        // set a server cookie with the new market
        const cookieResponse = yield setServerSideMarketCookie(action.market.marketId || '');

        if (cookieResponse.ok) {
            location.reload();
        }
    } else if (
        language !== action.language &&
        isBrowser
    ) {
        const {customerMode}: ISettingsContainerState = yield select(
            (s: IStoreState) => s.settings
        );
        const productId = currentUrl.searchParams.get("product");

        const [cfetchPageDataAvailable]: [
            OmitConfig<typeof fetchPageDataAvailable>
        ] = yield connectServiceFunctions(fetchPageDataAvailable);

        const pageResponse: Response = yield call(
            cfetchPageDataAvailable,
            productId ? productPageUrl : switchToLangUrlPathname,
            productId,
            customerMode
        );
        const pageAvailable = pageResponse.ok;

        if (pageAvailable) {
            // Add search parameters to the url if they exist for product pages
            location.href = switchToLangUrlPathname + switchSearchParams;
        } else {
            location.href = "/" + action.language;
        }
    }
}

function* setServerSideMarketCookie(marketId: string) {
    const [cSetMarketCookie]: [OmitConfig<typeof setMarketCookie>] = yield connectServiceFunctions(setMarketCookie);
    const cookieResponse: Response = yield call(cSetMarketCookie as any, marketId);
    return cookieResponse
}


function onSetCostumerMode(action: ISetCustomerModeSettingsAction) {
    if (isBrowser) {
        localStorage.setItem(Keys.CUSTOMER_TYPE, action.customerMode);
    }
}

export const settingsSagas = [
    fork(onPageInit),
    takeEvery(SettingsAction.PERSIST_SETTINS, onPersistSettings),
    takeEvery(LOCATION_CHANGE, onLocationChange),
    takeEvery(SettingsAction.CHANGE_LANGUAGE_AND_MARKET, onLanguageAndMarketChange),
    takeEvery(SettingsAction.SET_CUSTOMER_MODE, onSetCostumerMode),
];
